import BaseModalStore from '@src/components/modal-container/base-modal-store';
import Toast from '@src/components/toast';
import { noop } from '@src/utils/common-util';
import { observable } from 'mobx';
import { autobind } from 'core-decorators';

function getErrorMessage(error) {
  return error && typeof error.message === 'string' && error.message ? error.message : '创建队伍失败，请稍后重试';
}

class VeteranReviewCreateTeamModalStore extends BaseModalStore {
  @observable submitting = false;

  createTeamAndEnterPoster = noop;

  enterPoster = noop;

  appendCloseCallback = noop;

  appendInstanceGeneration = 0;

  submitPromise = null;

  constructor({ createTeamAndEnterPoster = noop, enterPoster = noop } = {}) {
    super();
    this.createTeamAndEnterPoster = createTeamAndEnterPoster;
    this.enterPoster = enterPoster;
  }

  /**
   * 绑定当前 useModal append 实例持有的关闭回调。
   * @param {Object} props 弹窗组件提供的视图契约。
   * @param {Function} props.appendCloseCallback 守卫关闭成功后卸载 append 持有的外层弹窗。
   * @returns {void}
   */
  use({ appendCloseCallback = noop } = {}) {
    super.use();
    this.bindAppendCloseCallback(appendCloseCallback);
  }

  bindAppendCloseCallback(appendCloseCallback = noop) {
    if (this.appendCloseCallback !== appendCloseCallback) {
      this.appendCloseCallback = appendCloseCallback;
      this.appendInstanceGeneration += 1;
    }
  }

  /** @returns {boolean} 弹窗是否已关闭；建队请求进行中返回 false。 */
  onClickClose = () => {
    return this.closeIfIdle();
  };

  /**
   * 提交进行中不关闭；否则同时关闭本地状态与 append 持有的外层弹窗。
   * @param {Function} closeModal useModal 遮罩路径使用的显式外层关闭回调。
   * @returns {boolean} 仅当本地状态与 append 生命周期都关闭时为 true。
   */
  closeIfIdle(closeModal = this.appendCloseCallback) {
    if (this.submitting) {
      return false;
    }
    this.isOpen = false;
    this.isActive = false;
    this.clearStyle();
    closeModal();
    return true;
  }

  /** 只关闭拥有已完成提交的那个 append 实例。 */
  closeSubmissionOwner(appendCloseCallback, appendInstanceGeneration) {
    if (this.appendCloseCallback !== appendCloseCallback || this.appendInstanceGeneration !== appendInstanceGeneration) {
      return false;
    }
    return this.closeIfIdle(appendCloseCallback);
  }

  /**
   * 共享同一条建队请求，仅在页面 owner 成功后才经 append 回调关闭。
   * @returns {Promise<undefined>} 共享的提交；失败时 Toast、解锁并保持两层弹窗打开。
   */
  @autobind
  handleCreateTeam() {
    if (this.submitPromise) {
      return this.submitPromise;
    }

    this.submitting = true;
    const submitAppendCloseCallback = this.appendCloseCallback;
    const submitAppendInstanceGeneration = this.appendInstanceGeneration;
    let submitResult;
    try {
      submitResult = this.createTeamAndEnterPoster();
    } catch (error) {
      submitResult = Promise.reject(error);
    }
    let request;
    request = Promise.resolve(submitResult)
      .then(result => {
        this.submitting = false;
        this.submitPromise = null;
        this.closeSubmissionOwner(submitAppendCloseCallback, submitAppendInstanceGeneration);
        return undefined;
      })
      .catch(error => {
        if (this.appendCloseCallback === submitAppendCloseCallback && this.appendInstanceGeneration === submitAppendInstanceGeneration) {
          Toast.fail(getErrorMessage(error));
        }
        if (this.submitPromise === request) {
          this.submitting = false;
          this.submitPromise = null;
        }
        return undefined;
      });

    this.submitPromise = request;
    return request;
  }

  /**
   * 对 useModal 的遮罩关闭回调应用同一 pending 守卫。
   * @param {Function} closeModal useModal 提供的 append 关闭回调。
   * @returns {boolean} 弹窗是否接受了关闭请求。
   */
  @autobind
  handleExternalClose(closeModal) {
    return this.closeIfIdle(closeModal);
  }

  /**
   * 关闭两层弹窗，并把无队伍的海报路由委托给注入的语义回调。
   * @returns {undefined} 路由仍是页面 owner 的副作用；提交进行中的尝试被忽略。
   */
  @autobind
  handleContinueWithoutTeam() {
    if (!this.closeIfIdle()) {
      return undefined;
    }
    this.enterPoster();
    return undefined;
  }
}

export default VeteranReviewCreateTeamModalStore;
